/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef, useState} from 'react';

export const RECONCILE_TIMEOUT_MS = 10000;

export interface SilentReconcileProps {
  run: () => boolean
}

export interface SilentReconcileResult {
  reconcile: () => void,
  settle: () => void,
  reconciling: boolean
}

/**
 * Runs a programmatic refetch and reports while one is in flight, so the
 * surfaces which paint a loading state can tell a refetch nobody asked for
 * apart from one the user started.
 *
 * `reconcile` runs immediately and every time it is called - there is no
 * coalescing, so a caller which can fire in bursts issues one request per
 * call. `run` reports whether a round actually began, since one which began
 * nothing must not raise a flag nothing will ever lower, and `settle` lowers
 * it when that round's own load lands, from wherever the caller observes it.
 *
 * Rounds may overlap. The flag is raised by each and lowered by the first
 * settle, so the tail of a later round is unsuppressed - brief, bounded by the
 * safety timeout, and the reason `settle` is not attributed to a round.
 *
 * ```typescript
 *
 *   import useSilentReconcile from './useSilentReconcile';
 *
 *   const {reconcile, settle, reconciling} = useSilentReconcile({
 *     run : () => {
 *       queryClient.invalidateQueries(key, {exact: true});
 *       gridRef.current?.refreshInfiniteCache?.();
 *       return !!gridRef.current;
 *     }
 *   });
 *
 *   // ... a change which may have invalidated a server answer ...
 *   reconcile();
 *
 *   // ... and, once the refetch this started has landed ...
 *   settle();
 *
 * ```
 *
 * @param   run Starts the refetch, returning whether a round began.
 * @returns `reconcile` to run one, `settle` to report the round landed, and
 *          `reconciling` while one is in flight.
 */
export const useSilentReconcile = ({run}: SilentReconcileProps): SilentReconcileResult => {
  const safety = useRef<ReturnType<typeof setTimeout>>(null);
  const runner = useRef(run);

  const raised                        = useRef(false);
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    runner.current = run;
  });

  const lower = useCallback((): void => {
    if (raised.current) {
      raised.current = false;
      clearTimeout(safety.current);
      setReconciling(false);
    }
  }, []);

  const reconcile = useCallback((): void => {
    if (runner.current()) {
      raised.current = true;
      clearTimeout(safety.current);
      safety.current = setTimeout(lower, RECONCILE_TIMEOUT_MS);
      setReconciling(true);
    }
  }, [lower]);

  useEffect(() => () => {
    clearTimeout(safety.current);
  }, []);

  return {reconcile, settle: lower, reconciling};
};

export default useSilentReconcile;
